<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, toRef, watch } from 'vue';
import HeroPromptInput from './HeroPromptInput.vue';
import { staticName } from '@/lib/asset';
import ImageLazy from '@/components/ImageLazy.vue';
import { Amplitude } from '@seede/amplitude';
import { i18n, t } from '~/i18n';
import { landingHomeEn } from '~/i18n/landing-home/en.js';

const locale = toRef(i18n.options, 'locale');

const lines = computed(() => {
  void locale.value;
  return [t('landing_home.hero.h1_line1'), t('landing_home.hero.h1_line2')];
});

/** 日文 Hero 标题：交给页面级 typography token，在 ja 下按英文基准放大 180% */
const heroTitleLineClass = computed(() =>
  locale.value === 'ja' ? 'block landing-title-hero' : 'block text-[clamp(1.1rem,2.5vw,2.5rem)]'
);

/** 日文副标题固定 16px；英文 md 及以上仍为 text-xl (20px) */
const heroSubtitleClass = computed(() =>
  locale.value === 'ja'
    ? 'mt-2 text-black/90 text-base leading-tight'
    : 'mt-2 text-black/90 text-base md:text-xl leading-tight'
);

// 动画状态
const isMobile = ref(false);

// 图片素材池
const imageSources = [
  // 9-16 目录
  '/static-assets/Deck.webp',
  '/static-assets/EventPost.webp',
  '/static-assets/EventPost02.webp',
  '/static-assets/InfoGraphic.webp',
  '/static-assets/MarketingDeck.webp',
  '/static-assets/Paper.webp',
  '/static-assets/ProductPost.webp',
  '/static-assets/album.webp',
  '/static-assets/card-bg/9-16/nike2.webp',
  '/static-assets/auto.webp',
  // 4-5 目录
  '/static-assets/card-bg/4-5/01.webp',
  '/static-assets/card-bg/4-5/02.webp',
  '/static-assets/card-bg/4-5/03.webp',
  '/static-assets/card-bg/4-5/04.webp',
  '/static-assets/card-bg/4-5/06.webp',
  '/static-assets/card-bg/4-5/art.webp',
  '/static-assets/card-bg/4-5/book.webp',
  '/static-assets/card-bg/4-5/book02.webp',
  '/static-assets/card-bg/4-5/concert.webp',
  '/static-assets/card-bg/4-5/event.webp',
  '/static-assets/card-bg/4-5/event02.webp',
  '/static-assets/card-bg/4-5/music.webp',
  // 3-5 目录
  '/static-assets/card-bg/3-5/01.webp',
  '/static-assets/card-bg/3-5/02.webp',
  '/static-assets/card-bg/3-5/03.webp',
  '/static-assets/card-bg/3-5/04.webp',
  '/static-assets/card-bg/3-5/05.webp',
  '/static-assets/card-bg/3-5/07.webp',
  '/static-assets/card-bg/3-5/08.webp',
  '/static-assets/card-bg/3-5/cover.webp',
  '/static-assets/card-bg/3-5/cover02.webp',
  '/static-assets/card-bg/3-5/cover03.webp',
  '/static-assets/card-bg/3-5/new01.webp',
  '/static-assets/card-bg/3-5/new02.webp',
  // 16-9 目录
  '/static-assets/card-bg/16-9/01.webp',
  '/static-assets/02.webp',
  '/static-assets/03.webp',
  '/static-assets/card-bg/16-9/04.webp',
  '/static-assets/05.webp',
  '/static-assets/06.webp',
  '/static-assets/card-bg/16-9/07.webp',
  '/static-assets/PPT.webp',
  // 1-1 目录
  '/static-assets/card-bg/1-1/01.webp',
  '/static-assets/squarePost.webp',
  '/static-assets/card-bg/1-1/02.webp',
  '/static-assets/card-bg/1-1/03.webp',
  '/static-assets/card-bg/1-1/04.webp',
  '/static-assets/ablum.webp',
  '/static-assets/albumGenZ.webp',
  '/static-assets/albumVibe.webp',
].map((src) => `${staticName}${src}`);

const FLYING_TEXT_SIZES = [
  'medium',
  'medium',
  'small',
  'small',
  'small',
  'small',
  'medium',
  'small',
  'small',
  'tiny',
  'tiny',
  'tiny',
  'tiny',
  'tiny',
] as const;

type FlyingTextSize = (typeof FLYING_TEXT_SIZES)[number];

interface FileTypeItem {
  type: string;
  label: string;
  color: string;
}

interface TextContentItem {
  text: string;
  size: FlyingTextSize;
}

const fileTypes = computed<FileTypeItem[]>(() => {
  void locale.value;
  const color = 'rgba(0, 0, 0, 0.6)';
  return [
    { type: 'word', label: t('landing_home.hero.file_type.doc'), color },
    { type: 'excel', label: t('landing_home.hero.file_type.xls'), color },
    { type: 'ppt', label: t('landing_home.hero.file_type.ppt'), color },
    { type: 'pdf', label: t('landing_home.hero.file_type.pdf'), color },
    { type: 'image', label: t('landing_home.hero.file_type.img'), color },
    { type: 'text', label: t('landing_home.hero.file_type.txt'), color },
    { type: 'markdown', label: t('landing_home.hero.file_type.md'), color },
  ];
});

/** 首屏动效卡片文案固定英文（演示设计稿为英文） */
const textContents = computed<TextContentItem[]>(() =>
  landingHomeEn.hero.flying_text.map((text, index) => ({
    text,
    size: FLYING_TEXT_SIZES[index] ?? 'small',
  }))
);

// 飞行卡片类型
interface FlyingCard {
  id: number;
  pathType: number; // 0-4 路径类型
  progressOffset: number; // 在路径上的进度偏移 (0-1)
  contentType: 'file' | 'text';
  fileType: FileTypeItem;
  textContent: TextContentItem;
  image: string;
}

const flyingCards = ref<FlyingCard[]>([]);
const cardRefs = ref<HTMLElement[]>([]);
const imageLoadedMap = ref<Record<number, boolean>>({});
let nextId = 0;
let animationFrameId: number | null = null;
let startTime = 0;

const trackMobileStartFree = () => {
  Amplitude.event('nav_link_click', {
    link_text: t('landing_home.hero.cta_mobile'),
    link_target: '/signup',
    section: 'hero_mobile',
    page_path: window.location.href,
  });
};

// 图片加载完成回调
const handleImageLoaded = (cardId: number) => {
  imageLoadedMap.value[cardId] = true;
};

// 计算贝塞尔曲线上的点和切线角度
function getBezierPoint(
  t: number,
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number }
): { x: number; y: number; angle: number } {
  const t1 = 1 - t;
  const t1Sq = t1 * t1;
  const tSq = t * t;

  // 三次贝塞尔曲线
  const x = t1Sq * t1 * p0.x + 3 * t1Sq * t * p1.x + 3 * t1 * tSq * p2.x + tSq * t * p3.x;
  const y = t1Sq * t1 * p0.y + 3 * t1Sq * t * p1.y + 3 * t1 * tSq * p2.y + tSq * t * p3.y;

  // 切线 (一阶导数)
  const dx = 3 * t1Sq * (p1.x - p0.x) + 6 * t1 * t * (p2.x - p1.x) + 3 * tSq * (p3.x - p2.x);
  const dy = 3 * t1Sq * (p1.y - p0.y) + 6 * t1 * t * (p2.y - p1.y) + 3 * tSq * (p3.y - p2.y);

  const angle = Math.atan2(dy, dx) * (180 / Math.PI);

  return { x, y, angle };
}

// 缓存路径
const cachedPaths = { value: [] as any[] };

const updatePaths = () => {
  if (typeof window === 'undefined') return;

  const screenHeight = window.innerHeight;
  const screenWidth = window.innerWidth;
  const centerX = screenWidth / 2;
  const centerY = screenHeight / 2 + 80;
  const margin = 150;

  const mobile = isMobile.value;
  const smallOffset = mobile ? screenWidth * 0.8 : screenWidth * 0.25;
  const largeOffset = mobile ? screenWidth * 0.6 : screenWidth * 0.5;

  const pathTop = -margin;
  const pathBottom = screenHeight + margin;

  cachedPaths.value = [
    // 路径 0: 中间垂直线
    {
      p0: { x: centerX, y: pathBottom },
      p1: { x: centerX, y: centerY + 200 },
      p2: { x: centerX, y: centerY - 200 },
      p3: { x: centerX, y: pathTop },
    },
    // 路径 1: 左侧小弧线
    {
      p0: { x: centerX - smallOffset, y: pathBottom },
      p1: { x: centerX, y: centerY + 150 },
      p2: { x: centerX, y: centerY - 150 },
      p3: { x: centerX - smallOffset, y: pathTop },
    },
    // 路径 2: 右侧小弧线
    {
      p0: { x: centerX + smallOffset, y: pathBottom },
      p1: { x: centerX, y: centerY + 150 },
      p2: { x: centerX, y: centerY - 150 },
      p3: { x: centerX + smallOffset, y: pathTop },
    },
    // 路径 3: 左侧大弧线
    {
      p0: { x: centerX - largeOffset, y: pathBottom },
      p1: { x: centerX, y: centerY + 100 },
      p2: { x: centerX, y: centerY - 100 },
      p3: { x: centerX - largeOffset, y: pathTop },
    },
    // 路径 4: 右侧大弧线
    {
      p0: { x: centerX + largeOffset, y: pathBottom },
      p1: { x: centerX, y: centerY + 100 },
      p2: { x: centerX, y: centerY - 100 },
      p3: { x: centerX + largeOffset, y: pathTop },
    },
  ];
};

// 计算卡片状态
const getCardState = (card: FlyingCard, paths: any[], time: number, mobile: boolean) => {
  const elapsed = time - startTime;
  const cycleDuration = mobile ? 7000 : 9000;
  const rawProgress = (elapsed / cycleDuration + card.progressOffset) % 1;

  const path = paths[card.pathType];
  if (!path) return null;

  const point = getBezierPoint(rawProgress, path.p0, path.p1, path.p2, path.p3);

  // 判断是文件卡片还是设计图片
  const isDesign = rawProgress > 0.5;
  const isOutermostPath = card.pathType === 3 || card.pathType === 4;

  // 计算透明度和模糊
  let opacity = 1;
  let blur = 0;

  if (!isDesign) {
    // 文件卡片: 中间最亮，两端淡出
    if (rawProgress < 0.1) {
      opacity = rawProgress / 0.1;
    } else if (rawProgress > 0.85) {
      opacity = (1 - rawProgress) / 0.15;
    }
  } else {
    // 设计图片
    const designProgress = (rawProgress - 0.6) / 0.1;

    if (isOutermostPath) {
      if (designProgress < 1) {
        opacity = Math.min(designProgress, 1);
      }
    } else {
      if (designProgress < 1) {
        opacity = Math.min(designProgress, 1);
        blur = 15 * (1 - designProgress);
      }

      if (rawProgress > 0.9) {
        opacity = (1 - rawProgress) / 0.1;
      }
    }
  }

  // 计算缩放
  let scale: number;
  if (!isDesign) {
    const fileProgress = rawProgress / 0.5;
    scale = 1.5 - fileProgress * 1.2;
  } else {
    const designProgress = (rawProgress - 0.5) / 0.5;
    scale = 0.2 + designProgress * 1;
  }

  return {
    x: point.x,
    y: point.y,
    angle: point.angle + 90,
    opacity,
    scale,
    blur,
    isDesign,
  };
};

// 初始化卡片
const initCards = () => {
  const cards: FlyingCard[] = [];
  const mobile = isMobile.value;
  const cardsPerPath = mobile ? 4 : 10;
  const pathTypes = mobile ? [0, 1, 2] : [0, 1, 2, 3, 4];

  const shuffledImages = [...imageSources].sort(() => Math.random() - 0.5);
  let imageIndex = 0;

  pathTypes.forEach((pathType) => {
    for (let i = 0; i < cardsPerPath; i++) {
      const isTextCard = Math.random() > 0.2;

      if (imageIndex >= shuffledImages.length) {
        shuffledImages.sort(() => Math.random() - 0.5);
        imageIndex = 0;
      }

      cards.push({
        id: nextId++,
        pathType,
        progressOffset: i / cardsPerPath + Math.random() * 0.1,
        contentType: isTextCard ? 'text' : 'file',
        fileType: fileTypes.value[Math.floor(Math.random() * fileTypes.value.length)]!,
        textContent: textContents.value[Math.floor(Math.random() * textContents.value.length)]!,
        image: shuffledImages[imageIndex++]!,
      });
    }
  });

  flyingCards.value = cards;
};

// 动画循环
const animate = () => {
  const time = Date.now();
  const paths = cachedPaths.value;
  const mobile = isMobile.value;
  const cards = flyingCards.value;
  const elements = cardRefs.value;

  if (paths.length && cards.length && elements.length) {
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const el = elements[i];
      if (!el) continue;

      const state = getCardState(card, paths, time, mobile);
      if (state) {
        el.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale}) rotate(${state.angle}deg)`;
        el.style.opacity = state.isDesign ? String(state.opacity) : String(state.opacity * 0.85);
        el.style.filter = state.blur > 0 ? `blur(${state.blur}px)` : 'none';

        // 切换设计面与文件/文本面的显示
        const designSide = el.querySelector('.design-side') as HTMLElement;
        const infoSide = el.querySelector('.info-side') as HTMLElement;
        if (designSide && infoSide) {
          if (state.isDesign) {
            designSide.style.display = 'flex';
            infoSide.style.display = 'none';
          } else {
            designSide.style.display = 'none';
            infoSide.style.display = 'flex';
          }
        }
      }
    }
  }

  animationFrameId = requestAnimationFrame(animate);
};

// Resize 优化
let resizeTimeout: any = null;
const handleResize = () => {
  if (resizeTimeout) clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    isMobile.value = window.innerWidth < 768;
    updatePaths();
  }, 200);
};

watch(locale, () => {
  if (flyingCards.value.length) {
    initCards();
  }
});

onMounted(() => {
  isMobile.value = window.innerWidth < 768;
  updatePaths();
  window.addEventListener('resize', handleResize);

  setTimeout(() => {
    startTime = Date.now();
    initCards();
    animationFrameId = requestAnimationFrame(animate);
  }, 300);
});

onUnmounted(() => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
  }
  window.removeEventListener('resize', handleResize);
  if (resizeTimeout) clearTimeout(resizeTimeout);
});
</script>

<template>
  <section class="relative h-screen flex items-center justify-center overflow-hidden bg-white">
    <!-- 飞行卡片容器 - 使用 data-clarity-ignore 屏蔽 Clarity SDK 对高频变动 DOM 的采集 -->
    <div
      class="absolute inset-0 pointer-events-none overflow-hidden"
      style="z-index: 5"
      data-clarity-ignore
      data-clarity-mask
    >
      <div
        v-for="card in flyingCards"
        :key="card.id"
        ref="cardRefs"
        class="card-base"
        :class="{ 'is-mobile': isMobile }"
        data-clarity-ignore
      >
        <!-- 设计图片面 -->
        <div
          class="design-side card-design absolute flex items-center justify-center overflow-hidden"
          :class="{ 'is-mobile': isMobile }"
        >
          <!-- 白色叠加渐变背景占位（图片未加载时显示） -->
          <div
            v-if="!imageLoadedMap[card.id]"
            class="image-placeholder-gradient absolute inset-0"
          />
          <ImageLazy
            :src="card.image"
            :alt="$t('landing_home.hero.flying_card_alt')"
            class="max-w-full max-h-full"
            image-class="max-w-full max-h-full object-contain"
            root-margin="400px"
            @load="handleImageLoaded(card.id)"
          />
        </div>

        <!-- 信息面 (文本或文件) -->
        <div class="info-side absolute">
          <!-- 文本卡片 -->
          <div
            v-if="card.contentType === 'text'"
            class="card-text"
            :class="[`size-${card.textContent.size}`, { 'is-mobile': isMobile }]"
          >
            <span class="text-content">
              {{ card.textContent.text }}
            </span>
          </div>

          <!-- 文件卡片 -->
          <div v-else class="card-file" :class="{ 'is-mobile': isMobile }">
            <!-- 文件图标 -->
            <div class="file-icon-container">
              <ImageLazy
                :src="`/docs/icons/${card.fileType.type}.svg`"
                :alt="card.fileType.label"
                class="w-full h-full"
                image-class="w-full h-full opacity-40 brightness-0 saturate-100"
                root-margin="200px"
              />
            </div>
            <div class="file-label" :style="{ color: card.fileType.color }">
              {{ card.fileType.label }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Content Container (Centered) -->
    <div class="relative z-10 w-full mx-auto px-4 md:px-8 flex flex-col items-center gap-8">
      <!-- Hero Title -->
      <div class="text-center max-w-[95%]">
        <h1
          class="font-boldonse text-black/90 uppercase leading-[1.6] tracking-tight landing-title-multiline"
        >
          <span
            v-for="(line, lineIndex) in lines"
            :key="lineIndex"
            :class="[heroTitleLineClass, 'landing-title-line']"
          >
            {{ line }}
          </span>
        </h1>
        <p :class="heroSubtitleClass">
          {{ $t('landing_home.hero.subtitle') }}
        </p>
      </div>

      <!-- Input Section - Desktop -->
      <HeroPromptInput class="hidden md:block" />

      <!-- Start Free Button - Mobile -->
      <NuxtLink
        to="/signup"
        class="md:hidden w-[calc(100%-2rem)] text-center py-3 rounded-full bg-[#ff6b35] text-white font-semibold text-base hover:bg-[#ff7d4d] transition-colors shadow-lg"
        @click="trackMobileStartFree"
      >
        {{ $t('landing_home.hero.cta_mobile') }}
      </NuxtLink>
    </div>
  </section>
</template>

<style scoped>
/* 基础卡片样式 */
.card-base {
  position: absolute;
  will-change: transform, opacity;
  pointer-events: none;
}

/* 设计图片卡片 */
.card-design {
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 102px;
  height: 128px;
  margin-left: -51px;
  margin-top: -64px;
  border-radius: 12px;
}
.card-design.is-mobile {
  width: 80px;
  height: 100px;
  margin-left: -40px;
  margin-top: -50px;
  border-radius: 8px;
}

/* 文本卡片 */
.card-text {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  background: #ffffff;
  border-radius: 12px;
}

.card-text.size-tiny {
  width: 100px;
  min-height: 48px;
  margin-left: -50px;
  margin-top: -24px;
  padding: 6px;
}
.card-text.size-small {
  width: 120px;
  min-height: 70px;
  margin-left: -60px;
  margin-top: -35px;
  padding: 8px;
}
.card-text.size-medium {
  width: 128px;
  min-height: 90px;
  margin-left: -64px;
  margin-top: -45px;
  padding: 10px;
}

.card-text.is-mobile.size-tiny {
  width: 80px;
  min-height: 40px;
  margin-left: -40px;
  margin-top: -20px;
  padding: 5px;
}
.card-text.is-mobile.size-small {
  width: 100px;
  min-height: 60px;
  margin-left: -50px;
  margin-top: -30px;
  padding: 6px;
}
.card-text.is-mobile.size-medium {
  width: 106px;
  min-height: 80px;
  margin-left: -53px;
  margin-top: -40px;
  padding: 8px;
}

.text-content {
  font-weight: 400;
  color: rgba(0, 0, 0, 0.4);
  text-align: left;
  line-height: 1.4;
  letter-spacing: -0.02em;
  display: block;
  word-break: break-word;
  overflow-wrap: break-word;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  padding: 12px;
  font-size: 14px;
}
.size-tiny .text-content {
  font-size: 10px;
}
.size-small .text-content {
  font-size: 12px;
}

.is-mobile .text-content {
  border-radius: 8px;
  padding: 8px;
  font-size: 11px;
}
.is-mobile.size-tiny .text-content {
  font-size: 9px;
}
.is-mobile.size-small .text-content {
  font-size: 10px;
}

/* 文件卡片 */
.card-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 118px;
  margin-left: -45px;
  margin-top: -59px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.card-file.is-mobile {
  width: 64px;
  height: 86px;
  margin-left: -32px;
  margin-top: -43px;
  border-radius: 8px;
}

.file-icon-container {
  margin-bottom: 0.5rem;
  width: 48px;
  height: 48px;
}
.is-mobile .file-icon-container {
  width: 32px;
  height: 32px;
}

.file-label {
  font-size: 14px;
  font-weight: 400;
  opacity: 0.4;
  letter-spacing: 0.5px;
}
.is-mobile .file-label {
  font-size: 12px;
}

/* 图片占位：白色叠加渐变背景 */
.image-placeholder-gradient {
  background: linear-gradient(135deg, rgb(255, 236, 230) 0%, rgba(237, 244, 255, 0.2) 50%), #ffffff;
  border-radius: inherit;
}
</style>
